import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { getUrl, showError } from '@utils';
import { useQuery } from 'react-query';
import { QUERY_KEYS } from 'constants/queryKeys';
import { serviceVehiclesServices, vehiclesServices } from '@services';
import { LIMIT } from '@constants';
import { URLS } from 'layouts/DrawerLayout/constants';
import { useAssignees } from 'hooks/common';
import { useSelector } from 'react-redux';
import { selectServiceVehiclesPinnedFilters } from '@store';
import {
  handleBrandsOptions,
  handleRequestTypesOptions,
  handleServiceVehiclesData,
  handleServiceVehiclesRequestPayload,
} from './utils';

export const SERVICE_VEHICLES_PAGE_SIZE = LIMIT;

export const useServiceVehiclesOperations = () => {
  const router = useRouter();
  const {
    assigneesWithNone,
    assigneesMap,
    isFetching: isAssigneesFetching,
  } = useAssignees();
  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || SERVICE_VEHICLES_PAGE_SIZE;
  const orderBy = router.query?.orderBy || '';
  const order = router.query?.order || '';
  const others = router.query;

  const otherQueries = Object.entries(others)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const pinnedFilters = useSelector(selectServiceVehiclesPinnedFilters);
  const hasFilters =
    pinnedFilters?.length > 0 || Object.keys(others).length > 0;

  const serviceVehiclesResponse = useQuery(
    [
      QUERY_KEYS.GET_SERVICE_VEHICLES_KEY,
      {
        page,
        pageSize,
        orderBy,
        order,
        otherQueries,
      },
    ],
    () => {
      const payload = handleServiceVehiclesRequestPayload(router);
      return serviceVehiclesServices?.getServiceVehiclesFilters({
        page,
        pageSize: parseInt(pageSize, 10) || SERVICE_VEHICLES_PAGE_SIZE,
        orderBy,
        order,
        ...payload,
      });
    }
  );

  const serviceVehiclesEnumsResponse = useQuery(
    QUERY_KEYS.GET_SERVICE_VEHICLES_ENUMS_KEY,
    () => serviceVehiclesServices?.getServiceVehiclesEnums()
  );

  const requestTypesOptions = useMemo(
    () =>
      handleRequestTypesOptions(
        serviceVehiclesEnumsResponse?.data?.data?.types
      ),
    [serviceVehiclesEnumsResponse?.data?.data?.types]
  );

  const brandsResponse = useQuery(
    QUERY_KEYS.GET_SERVICE_VEHICLES_BRANDS_KEY,
    () => vehiclesServices?.getVehicleEnums()
  );

  const brandsOptions = useMemo(
    () => handleBrandsOptions(brandsResponse?.data?.data?.models),
    [brandsResponse?.data?.data?.models]
  );

  const serviceVehicles = handleServiceVehiclesData({
    serviceVehicles: serviceVehiclesResponse?.data,
    serviceVehiclesTypes: serviceVehiclesEnumsResponse?.data?.data?.types,
    requestTypesOptions,
    brandsOptions,
    assigneesWithNone,
    assigneesMap,
  });

  const handleSortBy = (sortByModel) => {
    const url = getUrl(
      { ...router.query, ...sortByModel },
      URLS.serviceVehicles
    );
    router.push(url);
  };

  const handlePageChange = (newPage, newPageSize) => {
    const currentPage = router.query.page || '1';
    const currentPageSize =
      router.query.pageSize || SERVICE_VEHICLES_PAGE_SIZE.toString();

    const urlParams = { ...router.query };

    if (newPage !== currentPage) {
      urlParams.page = newPage;
    }

    if (newPageSize && newPageSize.toString() !== currentPageSize) {
      urlParams.pageSize = newPageSize;
      urlParams.page = 1;
    }

    router.push(getUrl(urlParams, URLS.serviceVehicles));
  };

  useEffect(() => {
    if (brandsResponse?.isError) {
      showError(brandsResponse.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandsResponse?.isError]);

  useEffect(() => {
    if (serviceVehiclesResponse?.isError) {
      showError(serviceVehiclesResponse.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceVehiclesResponse?.isError]);

  useEffect(() => {
    if (serviceVehiclesEnumsResponse?.isError) {
      showError(serviceVehiclesEnumsResponse.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceVehiclesEnumsResponse?.isError]);

  return {
    page,
    total: serviceVehiclesResponse?.data?.meta?.total,
    loading:
      serviceVehiclesResponse?.isFetching ||
      serviceVehiclesEnumsResponse?.isFetching ||
      brandsResponse?.isFetching ||
      isAssigneesFetching,
    serviceVehicles,
    hasFilters,
    pageSize: parseInt(pageSize, 10) || SERVICE_VEHICLES_PAGE_SIZE,
    getServiceVehicles: serviceVehiclesResponse?.refetch,
    nextPage: handlePageChange,
    sortBy: handleSortBy,
  };
};
